import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyText from 'LuxKit/Typography/BodyText'
import LineExpandAltIcon from 'LuxKit/Icons/line/LineExpandAltIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { Heading4Style, Heading5Style } from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import type { DrawerSessionPair } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useDrawerAskAI'

function getTextContent(message: MessageItem): string {
  return message.parts
    .filter(p => p.type === 'text')
    .map(p => p.content)
    .join('')
}

function getUserDisplayText(message: MessageItem): string {
  return getTextContent(message)
    .replace(/\n\n<Context[^>]*>[\s\S]*?<\/Context>/gi, '')
}

const COLLAPSED_MAX_HEIGHT = 116
const BUBBLE_PADDING = 16

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(${rem(8)}); }
  to { opacity: 1; transform: translateY(0); }
`

const Container = styled.div`
  animation: ${fadeIn} 0.2s ease-out;
`

const BUBBLE_BOX_SHADOW = `0 ${rem(10)} ${rem(15)} ${rem(-3)} rgba(0, 0, 0, 0.1),
              0 ${rem(4)} ${rem(6)} ${rem(-4)} rgba(0, 0, 0, 0.1)`

const BubbleBase = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${rem(16)};
  box-shadow: ${BUBBLE_BOX_SHADOW};
`

const CollapsedBubble = styled(BubbleBase)`
  overflow: hidden;
`

const CollapsedContent = styled.div`
  padding: 0 ${rem(BUBBLE_PADDING)} ${rem(BUBBLE_PADDING)};
  max-height: ${rem(COLLAPSED_MAX_HEIGHT - 40)};
  overflow-y: auto;
  cursor: pointer;
`

const ExpandedBubble = styled(BubbleBase)`
  overflow: hidden;
`

const BubbleHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${rem(8)} ${rem(12)} 0;
`

const CollapsedHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(8)} ${rem(12)} 0;
`

const ExpandLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  color: ${props => props.theme.palette.neutral.default.three};
  cursor: pointer;
`

const ExpandedContent = styled.div`
  padding: 0 ${rem(BUBBLE_PADDING)} ${rem(BUBBLE_PADDING)};
  max-height: ${rem(350)};
  overflow-y: auto;
`

const ChatMarkdownWrapper = styled.div`
  h2 {
    ${Heading4Style}
  }
  h3 {
    ${Heading5Style}
  }
`

const UserMessageRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

const UserBubble = styled.div`
  max-width: 75%;
  padding: ${rem(8)} ${rem(18)};
  border-radius: ${rem(18)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  color: ${props => props.theme.palette.neutral.default.two};
  overflow-wrap: break-word;
  white-space: pre-wrap;
`

const AssistantMessageRow = styled.div``

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`

const LoadingDots = styled.div`
  display: flex;
  justify-content: center;
  gap: ${rem(4)};
  padding: ${rem(4)} 0;

  span {
    width: ${rem(6)};
    height: ${rem(6)};
    border-radius: 50%;
    background-color: ${props => props.theme.palette.neutral.default.three};
    animation: ${pulse} 1.4s infinite;

    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`

interface Props {
  latestResponse: MessageItem | null
  sessionHistory: Array<DrawerSessionPair>
  isResponseStreaming: boolean
  isWaitingForResponse: boolean
}

function DrawerAskAIBubble({
  latestResponse,
  sessionHistory,
  isResponseStreaming,
  isWaitingForResponse,
}: Props) {
  const intl = useIntl()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isClosed, setIsClosed] = useState(false)
  const expandedContentRef = useRef<HTMLDivElement>(null)

  const handleExpand = useCallback(() => {
    setIsExpanded(true)
  }, [])

  const handleCollapse = useCallback(() => {
    setIsExpanded(false)
  }, [])

  const handleClose = useCallback(() => {
    setIsClosed(true)
  }, [])

  useEffect(() => {
    if (isExpanded && isResponseStreaming && expandedContentRef.current) {
      expandedContentRef.current.scrollTop = expandedContentRef.current.scrollHeight
    }
  }, [isExpanded, isResponseStreaming, latestResponse?.parts])

  const loadingIndicator = useMemo(() => (
    <LoadingDots>
      <span />
      <span />
      <span />
    </LoadingDots>
  ), [])

  const renderAssistantContent = useCallback((message: MessageItem) => {
    const textContent = getTextContent(message)

    if (!textContent.trim()) {
      return loadingIndicator
    }

    return (
      <ChatMarkdownWrapper>
        <MarkdownRender
          content={textContent}
          fontSize="medium"
          type="compact"
        />
      </ChatMarkdownWrapper>
    )
  }, [loadingIndicator])

  if (isClosed || (!isWaitingForResponse && !latestResponse)) {
    return null
  }

  if (!isExpanded) {
    return (
      <Container>
        <CollapsedBubble>
          <CollapsedHeader>
            <ExpandLabel onClick={handleExpand}>
              <BodyText variant="small" colour="neutral-three">
                {intl.formatMessage({
                  id: 'mrYgT0Af3RludPEgMzi6',
                  defaultMessage: 'Expand',
                  description: 'Label to expand the AI assistant chat bubble in the trip planner AI chat',
                })}
              </BodyText>
              <LineExpandAltIcon size="XS" />
            </ExpandLabel>
            <IconButton
              kind="tertiary"
              variant="dark"
              size="small"
              onClick={handleClose}
              aria-label={intl.formatMessage({
                id: '3j7IQmGegtc_a7v3bKD7',
                defaultMessage: 'Close bubble',
                description: 'Aria label for button to close the AI assistant chat bubble',
              })}
            >
              <LineTimesIcon />
            </IconButton>
          </CollapsedHeader>
          <CollapsedContent onClick={handleExpand}>
            {isWaitingForResponse && loadingIndicator}
            {latestResponse && renderAssistantContent(latestResponse)}
          </CollapsedContent>
        </CollapsedBubble>
      </Container>
    )
  }

  return (
    <Container>
      <ExpandedBubble>
        <BubbleHeader>
          <IconButton
            kind="tertiary"
            variant="dark"
            size="small"
            onClick={handleCollapse}
            aria-label={intl.formatMessage({
              id: '_OB07s5YFuUCv8ih_9Bx',
              defaultMessage: 'Collapse bubble',
              description: 'Aria label for button to collapse the expanded AI assistant chat bubble',
            })}
          >
            <LineTimesIcon />
          </IconButton>
        </BubbleHeader>
        <ExpandedContent ref={expandedContentRef}>
          <Group direction="vertical" gap={24}>
            {sessionHistory.map((pair) => (
              <Group direction="vertical" gap={8} key={pair.question.id}>
                <UserMessageRow>
                  <UserBubble>
                    <BodyText variant="medium" colour="neutral-two">
                      {getUserDisplayText(pair.question)}
                    </BodyText>
                  </UserBubble>
                </UserMessageRow>
                <AssistantMessageRow>
                  {pair.answer ? renderAssistantContent(pair.answer) : loadingIndicator}
                </AssistantMessageRow>
              </Group>
            ))}
          </Group>
        </ExpandedContent>
      </ExpandedBubble>
    </Container>
  )
}

DrawerAskAIBubble.displayName = 'DrawerAskAIBubble'

export default DrawerAskAIBubble
